// /src/pages/admin/index.jsx
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import AdminShellOnfit from "../../components/admin/AdminShellOnfit";
import MemberUpdate from "./member-update";
import Measurements from "./measurements";
import WorkoutSettings from "./workout-settings";
import { AdminAuthProvider, useAdminAuth } from "../../hooks/useAdminAuth";
import { adminRegister } from "../../api/admin";
import { registerUser, registerUserForm } from "../../api/user";
import clsx from "clsx";
import { CheckIcon } from "@heroicons/react/24/solid";
import { isName, isPhone, isRRNFront } from "../../utils/validate";
import { formatPhone } from "../../utils/fomatPhone";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon as HICheck } from "@heroicons/react/20/solid";
import { createWorkplace, deleteWorkplace, fetchWorkplaces, updateWorkplace } from "../../api/adminWorkplace";


const WORKPLACES = ["온핏 강남점", "온핏 신촌점", "온핏 판교점"];
function parseFieldErrors(err) {
  const out = { fields: {}, global: "" };
  const detail = err?.data?.detail ?? err?.detail ?? err?.message ?? err;

  if (!detail) return out;
  if (typeof detail === "string") {
    out.global = detail;
    return out;
  }

  const items = Array.isArray(detail) ? detail : [detail];
  for (const it of items) {
    const msg = it?.msg || JSON.stringify(it);
    const loc = it?.loc;
    if (Array.isArray(loc) && loc.length) {
      const field = String(loc[loc.length - 1]);
      out.fields[field] = out.fields[field] ? `${out.fields[field]}\n${msg}` : msg;
    } else {
      out.global = out.global ? `${out.global}\n${msg}` : msg;
    }
  }
  return out;
}

function formatApiError(err, fallback = "요청에 실패했습니다.") {
  try {
    const detail = err?.data?.detail ?? err?.detail ?? err?.message ?? err;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((i) => i?.msg || JSON.stringify(i)).join("\n");
    if (typeof detail === "object" && detail?.msg) return detail.msg;
    return typeof detail === "object" ? JSON.stringify(detail) : String(detail);
  } catch {
    return fallback;
  }
}


function Shell({ children }) {
  return (
    <AdminShellOnfit>
      {children}
    </AdminShellOnfit>
  );
}


function Card({ title, children, className = "" }) {
  return (
    <div className={`w-full rounded-xl border border-gray-200 bg-white p-4 md:p-6 ${className}`}>
      {title && <div className="font-semibold mb-2">{title}</div>}
      {/* <p className="text-xs md:text-sm text-[#4A8AFF] mt-1">
            본인 확인을 위해 회원님의 고유 정보가 필요합니다.
          </p> */}
      {children}
    </div>
  );
}



function AdminCreateForm() {
  function toKoMsg(it) {
    const t = it?.type;
    const c = it?.ctx || {};
    const pat = c?.pattern || "";
    switch (t) {
      case "missing": return "필수 값입니다.";
      case "string_type": return "문자열이어야 합니다.";
      case "string_too_short": return `최소 ${c.min_length ?? ""}자 이상이어야 합니다.`;
      case "string_too_long": return `최대 ${c.max_length ?? ""}자 이하여야 합니다.`;
      case "string_pattern_mismatch":
        if (pat === "^\\d{6}$") return "주민번호 앞 6자리를 입력하세요 (yymmdd).";
        if (pat === "^\\d$") return "주민번호 뒤 1자리를 입력하세요.";
        if (pat === "^01[016789]-?\\d{3,4}-?\\d{4}$") return "휴대폰 번호 형식이 올바르지 않습니다.";
        return "형식이 올바르지 않습니다.";
      case "int_type":
      case "int_parsing": return "정수형 숫자여야 합니다.";
      case "bool_type":
      case "bool_parsing": return "true/false 값이어야 합니다.";
      default: return it?.msg || "유효하지 않은 값입니다.";
    }
  }

  function parseFieldErrors(err) {
    const out = { fields: {}, global: "" };
    const detail = err?.data?.detail ?? err?.detail ?? err?.message ?? err;
    if (!detail) return out;
    if (typeof detail === "string") { out.global = detail; return out; }
    const items = Array.isArray(detail) ? detail : [detail];
    for (const it of items) {
      const msg = toKoMsg(it);
      const loc = it?.loc;
      if (Array.isArray(loc) && loc.length) {
        const field = String(loc[loc.length - 1]);
        out.fields[field] = out.fields[field] ? `${out.fields[field]}\n${msg}` : msg;
      } else {
        out.global = out.global ? `${out.global}\n${msg}` : msg;
      }
    }
    return out;
  }

  function formatApiError(err, fallback = "요청에 실패했습니다.") {
    try {
      const detail = err?.data?.detail ?? err?.detail ?? err?.message ?? err;
      if (!detail) return fallback;
      if (typeof detail === "string") return detail;
      const items = Array.isArray(detail) ? detail : [detail];
      return items.map(toKoMsg).join("\n");
    } catch { return fallback; }
  }

  const { user } = useAdminAuth();
  const isSuper = user?.role === "super";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [makeSuper, setMakeSuper] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [okMsg, setOkMsg] = useState("");

  const [workplaceOptions, setWorkplaceOptions] = useState([]);
  const [wpLoading, setWpLoading] = useState(false);
  const [wpErr, setWpErr] = useState("");

  useEffect(() => {
    if (!isSuper) return;

    const load = async () => {
      try {
        setWpLoading(true);
        setWpErr("");
        const data = await fetchWorkplaces(); // GET /workplaces
        const active = Array.isArray(data)
          ? data.filter((w) => w.is_active)
          : [];
        setWorkplaceOptions(active);
      } catch (e) {
        setWpErr(formatApiError(e, "사업장 목록을 불러오지 못했습니다."));
      } finally {
        setWpLoading(false);
      }
    };

    load();
  }, [isSuper]);

  if (!isSuper) return null;


  const canSubmit = username.trim() && password.trim() && workplace.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setOkMsg(""); setFe({});
    const localFe = {};
    if (!username.trim()) localFe.username = "아이디를 입력하세요.";
    if (!password.trim()) localFe.password = "비밀번호를 입력하세요.";
    if (!workplace.trim()) localFe.workplace = "사업장을 선택하세요.";
    if (Object.keys(localFe).length) { setFe(localFe); return; }

    try {
      setLoading(true);
      await adminRegister({ username, password, workplace, is_super: !!makeSuper });
      setOkMsg("관리자 계정을 생성했습니다.");
      setUsername(""); setPassword(""); setWorkplace(""); setMakeSuper(false);
    } catch (e) {
      const { fields, global } = parseFieldErrors(e);
      setFe(fields);
      setErr(global || "생성에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="관리자 계정 생성">
      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* 아이디 */}
        <div>
          <label className="block text-sm md:text-base font-medium mb-1">아이디</label>
          <input
            className="w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
            value={username}
            onChange={(e) => { setUsername(e.target.value); if (fe.username) setFe((p) => ({ ...p, username: "" })); }}
            placeholder="admin id"
          />
          {fe.username && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.username}</p>}
        </div>

        {/* 비밀번호 */}
        <div>
          <label className="block text-sm md:text-base font-medium mb-1">비밀번호</label>
          <input
            type="password"
            className="w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (fe.password) setFe((p) => ({ ...p, password: "" })); }}
            placeholder="password"
          />
          {fe.password && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.password}</p>}
        </div>

        {/* 사업장 (Listbox) */}
        <div>
          <label className="block text-sm md:text-base font-medium mb-1">사업장</label>
          <Listbox
            value={workplace}
            onChange={(v) => { setWorkplace(v); if (fe.workplace) setFe((p) => ({ ...p, workplace: "" })); }}
          >
            <div className="relative">
              <Listbox.Button className="relative w-full border rounded-lg bg-[#F1F1F1] px-3 pr-9 py-2.5 text-left text-sm md:text-base focus:outline-none">
                <span className={clsx("block truncate", workplace ? "text-gray-900" : "text-gray-400")}>
                  {workplace || "선택"}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>

              <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border bg-white py-1 shadow-lg focus:outline-none">
                  {workplaceOptions.map((wp) => (
                    <Listbox.Option
                      key={wp.id}
                      value={wp.name} // 🔹 name을 선택값으로 사용 (adminRegister의 workplace 필드로 전송)
                      className={({ active }) =>
                        clsx(
                          "cursor-pointer select-none px-3 py-2 text-sm",
                          active ? "bg-blue-50 text-blue-700" : "text-gray-900"
                        )
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-center gap-2">
                          {selected ? <HICheck className="h-4 w-4 text-blue-600" /> : <span className="h-4 w-4" />}
                          <span className="truncate">{wp.name}</span>
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          {fe.workplace && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.workplace}</p>}
        </div>

        {/* 슈퍼관리자 체크 */}
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm md:text-base">
            <input
              type="checkbox"
              checked={makeSuper}
              onChange={(e) => setMakeSuper(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-[#5F9BFF]"
            />
            슈퍼관리자로 생성
          </label>
        </div>

        {/* 전역 메시지 */}
        {err && <p className="sm:col-span-2 text-red-600 text-sm whitespace-pre-line">{err}</p>}
        {okMsg && <p className="sm:col-span-2 text-green-600 text-sm">{okMsg}</p>}

        {/* 제출 버튼: 우측 하단 정렬 */}
        <div className="sm:col-span-2 flex justify-end">
          <button
            className="min-w-[120px] px-4 py-3 rounded-xl bg-[#5F9BFF] text-white disabled:bg-[#F1F1F1] disabled:text-gray-400"
            disabled={loading || !canSubmit}
          >
            {loading ? "생성 중..." : "관리자 생성"}
          </button>
        </div>
      </form>
    </Card>
  );
}

function UserCreateForm() {
  const { user } = useAdminAuth();
  const isSuper = user?.role === "super";
  const myWorkplace = user?.profile?.workplace || ""; // 일반관리자는 고정
  const inputBase = "w-full h-10 border rounded-lg px-3 bg-[#F1F1F1] outline-none text-sm md:text-base";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [front, setFront] = useState(""); // 주민번호 앞 6
  const [back, setBack] = useState("");   // 주민번호 뒤 1
  const [agree, setAgree] = useState({
    all: false,
    personal: false,
    tos: false,
    marketing: false,
  });

  const backRef = useRef(null);
  const nameValid = isName(name);
  const phoneValid = isPhone(phone);
  const frontValid = isRRNFront(front);
  const backValid = /^[0-9]$/.test(back);
  const rrnValid = frontValid && backValid;
  const rrnTouched = front.length > 0 || back.length > 0;
  const requiredAgree = agree.personal && agree.tos;

  // Admin 전용 추가 필드
  const [password, setPassword] = useState("");
  const [workplace, setWorkplace] = useState(isSuper ? "" : myWorkplace);

  // 이미지 업로드 (기존 기능 유지)
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");

  // 에러/진행 상태
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [okMsg, setOkMsg] = useState("");

  // 전체 제출 가능 여부
  const canSubmit =
    nameValid &&
    phoneValid &&
    rrnValid &&
    requiredAgree &&
    password.trim() &&
    (isSuper ? workplace.trim() : !!myWorkplace);

  // 동의 토글 (디자인 그대로)
  const toggleAgree = (key) => {
    if (key === "all") {
      const nextVal = !agree.all;
      setAgree({
        all: nextVal,
        personal: nextVal,
        tos: nextVal,
        marketing: nextVal,
      });
    } else {
      const next = { ...agree, [key]: !agree[key] };
      next.all = next.personal && next.tos && next.marketing;
      setAgree(next);
    }
  };

  // 입력 핸들러 (디자인 그대로)
  const handleFrontChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFront(v);
    if (v.length === 6) backRef.current?.focus();
  };
  const handleBackChange = (e) => {
    setBack(e.target.value.replace(/\D/g, "").slice(0, 1));
  };

  // 파일 업로드 (기존 유지)
  const onImageChange = (e) => {
    const f = e.target.files?.[0];
    setImage(null);
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return ""; });
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) {
      setFe((p) => ({ ...p, image: "이미지 파일만 업로드 가능합니다. (jpg/png/webp/gif)" }));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setFe((p) => ({ ...p, image: "최대 5MB까지 업로드 가능합니다." }));
      return;
    }
    setFe((p) => ({ ...p, image: "" }));
    setImage(f);
    setPreview(URL.createObjectURL(f));
  };
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  // 제출
  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setOkMsg(""); setFe({});

    // 1차 클라 에러 (필드별 메시지는 짧게 유지)
    const localFe = {};
    if (!nameValid) localFe.name = "이름 형식을 확인하세요.";
    if (!frontValid) localFe.rrn_front = "주민번호 앞 6자리(yymmdd).";
    if (!backValid) localFe.rrn_back1 = "주민번호 뒤 1자리.";
    if (!phoneValid) localFe.phone = "휴대폰 번호 형식이 올바르지 않습니다.";
    if (!password.trim()) localFe.password = "비밀번호를 입력하세요.";
    if (!agree.personal) localFe.consent_personal = "개인정보 동의(필수).";
    if (!agree.tos) localFe.consent_tos = "이용 약관 동의(필수).";
    if (isSuper ? !workplace.trim() : !myWorkplace) localFe.workplace = "사업장을 선택하세요.";
    if (Object.keys(localFe).length) { setFe(localFe); return; }

    try {
      setLoading(true);
      const phoneDigits = phone.replace(/\D/g, "");
      await registerUserForm(
        {
          name,
          rrn_front: front,
          rrn_back1: back,
          phone: phoneDigits,
          password,
          consent_personal: agree.personal,
          consent_tos: agree.tos,
          consent_marketing: agree.marketing,
          workplace: isSuper ? workplace : myWorkplace,
        },
        image
      );
      setOkMsg("회원이 등록되었습니다.");
      // 리셋
      setName(""); setPhone(""); setFront(""); setBack(""); setPassword("");
      setAgree({ all: false, personal: false, tos: false, marketing: false });
      setWorkplace(isSuper ? "" : myWorkplace);
      setImage(null);
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return ""; });
    } catch (e) {
      const { fields, global } = parseFieldErrors(e);
      setFe(fields);
      setErr(global || formatApiError(e, "등록에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  // 체크박스 UI (디자인 그대로)
  const Checkbox = ({ id }) => (
    <span
      onClick={() => toggleAgree(id)}
      className={clsx(
        "w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-colors",
        agree[id] ? "bg-[#5F9BFF]" : "bg-white border border-gray-300"
      )}
    >
      {agree[id] && <CheckIcon className="w-4 h-4 text-white" />}
    </span>
  );

  return (
    <Card title="일반 회원 등록" children="본인 확인을 위해 회원님의 고유 정보가 필요합니다.">
      <form onSubmit={onSubmit} className="flex flex-col">


        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">
              이름
              {!nameValid && name && (
                <span className="ml-4 text-[#DE4F4F] text-xs md:text-sm">
                  필수 항목이 제대로 입력되지 않았습니다.
                </span>
              )}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); if (fe.name) setFe((p) => ({ ...p, name: "" })); }}
              placeholder="홍길동"
              className={clsx(
                "w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base",
                !nameValid && name && "border-[#DE4F4F]"
              )}
            />
            {fe.name && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.name}</p>}
          </div>

          {/* 주민등록번호 */}
          <div className="flex flex-col">
            <label className="block text-sm md:text-base font-medium mb-1">
              주민등록번호
              {rrnTouched && !rrnValid && (
                <span className="ml-4 text-[#DE4F4F] text-xs md:text-sm">
                  필수 항목이 제대로 입력되지 않았습니다.
                </span>
              )}
            </label>

            <div className="flex items-center gap-2">
              {/* 앞 6자리 */}
              <input
                type="text"
                value={front}
                onChange={handleFrontChange}
                placeholder="앞 6자리 입력"
                maxLength={6}
                className={clsx(
                  "w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base",
                  front && !frontValid && "border-[#DE4F4F]"
                )}
              />

              <span className="text-sm text-[#DBDBDB]">–</span>

              {/* 뒤 1자리 + ●●●●●● 표시 */}
              <div className="relative w-full">
                <input
                  type="text"
                  value={back}
                  onChange={handleBackChange}
                  maxLength={1}
                  ref={backRef}
                  className={clsx(
                    "w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base",
                    "text-transparent caret-black",
                    back && !backValid && "border-[#DE4F4F]"
                  )}
                />
                {back && (
                  <span className="absolute inset-0 flex items-center px-3 pointer-events-none select-none">
                    {back + "●".repeat(6)}
                  </span>
                )}
              </div>
            </div>

            {(fe.rrn_front || fe.rrn_back1) && (
              <p className="text-red-600 text-xs mt-1 whitespace-pre-line">
                {[fe.rrn_front, fe.rrn_back1].filter(Boolean).join("\n")}
              </p>
            )}
          </div>

          {/* 휴대폰 */}
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">
              전화번호
              {!phoneValid && phone && (
                <span className="ml-4 text-[#DE4F4F] text-xs md:text-sm">
                  필수 항목이 제대로 입력되지 않았습니다.
                </span>
              )}
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(formatPhone(e.target.value));
                if (fe.phone) setFe((p) => ({ ...p, phone: "" }));
              }}
              placeholder="010-1234-5678"
              className={clsx(
                "w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base",
                !phoneValid && phone && "border-[#DE4F4F]"
              )}
            />
            {fe.phone && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.phone}</p>}
          </div>

          {/* 임시 비밀번호 */}
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">임시 비밀번호</label>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (fe.password) setFe((p) => ({ ...p, password: "" })); }}
              placeholder="최소 1자 이상"
              className={clsx(
                "w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base",

              )}
            />
            {fe.password && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.password}</p>}
          </div>


          <div>
            <label className="block text-sm md:text-base font-medium mb-1 ">사업장</label>

            {isSuper ? (
              <Listbox
                value={workplace}
                onChange={(v) => { setWorkplace(v); if (fe.workplace) setFe((p) => ({ ...p, workplace: "" })); }}
              >
                <div className="relative">
                  <Listbox.Button
                    className="relative w-full border rounded-lg bg-[#F1F1F1] px-3 pr-9 py-2.5 text-left text-sm md:text-base focus:outline-none"
                  >
                    <span className={clsx("block truncate ", workplace ? "text-gray-900" : "text-gray-400")}>
                      {workplace || "선택"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                    </span>
                  </Listbox.Button>

                  <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border bg-white py-1 shadow-lg focus:outline-none">
                      {WORKPLACES.map((w) => (
                        <Listbox.Option
                          key={w}
                          value={w}
                          className={({ active }) =>
                            clsx("cursor-pointer select-none px-3 py-2 text-sm", active ? "bg-blue-50 text-blue-700" : "text-gray-900")
                          }
                        >
                          {({ selected }) => (
                            <div className="flex items-center gap-2">
                              {selected ? <HICheck className="h-4 w-4 text-blue-600" /> : <span className="h-4 w-4" />}
                              <span className="truncate">{w}</span>
                            </div>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            ) : (
              <input
                className="w-full h-10 border rounded-lg px-3 bg-gray-100 outline-none text-sm md:text-base"
                value={myWorkplace || ""}
                readOnly
              />
            )}

            {fe.workplace && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.workplace}</p>}
          </div>



          {/* 프로필 이미지 (선택) */}
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">프로필 이미지 (선택)</label>

            <div className="flex items-center gap-3 flex-nowrap min-w-0">
              {/* 파일 입력: 숨김 */}
              <input
                id="profileFile"
                type="file"
                accept="image/*"
                onChange={onImageChange}
                className="sr-only"
              />

              {/* 보이는 커스텀 버튼 + 파일명 + 미리보기/삭제 */}
              <div className="flex items-center gap-3 min-w-0">
                <label
                  htmlFor="profileFile"
                  className="inline-flex items-center px-4 py-3 rounded-lg border border-gray-200 bg-white
               text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                >
                  파일 선택
                </label>

                {/* 파일명 */}
                <span className="min-w-0 flex-1 truncate text-sm text-gray-600">
                  {image?.name || ""}
                </span>

                {preview && (
                  <>
                    <img
                      src={preview}
                      alt="미리보기"
                      className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover border flex-shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImage(null);
                        setPreview((old) => { if (old) URL.revokeObjectURL(old); return ""; });
                      }}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white whitespace-nowrap flex-shrink-0"
                    >
                      이미지 제거
                    </button>
                  </>
                )}
              </div>

            </div>

            {fe.image && <p className="text-red-600 text-xs mt-1">{fe.image}</p>}
          </div>


          {/* 약관 동의(디자인 동일) */}
          <div className="flex flex-col gap-3 pt-1">
            <p className="font-medium text-sm md:text-base">서비스 약관 동의</p>

            <label className="flex items-center gap-3 w-full cursor-pointer select-none">
              <Checkbox id="all" />
              <span className="text-[#5F9BFF] font-semibold text-sm md:text-base">약관 전체 동의</span>
            </label>

            <hr className="border-gray-200 my-1" />

            <label className="flex items-center gap-3 w-full cursor-pointer select-none">
              <span className="flex-shrink-0"><Checkbox id="personal" /></span>
              <span className="flex-1 break-words text-sm md:text-base">
                [필수] 개인정보 제공 동의 – 회원번호, 이메일 주소, 이름, 성별, 연락처, 생년월일, 생일
              </span>
            </label>
            {fe.consent_personal && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.consent_personal}</p>}

            <label className="flex items-center gap-3 w-full cursor-pointer select-none">
              <span className="flex-shrink-0"><Checkbox id="tos" /></span>
              <span className="text-sm md:text-base">[필수] 서비스 약관 이용 동의</span>
            </label>
            {fe.consent_tos && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{fe.consent_tos}</p>}

            <label className="flex items-center gap-3 w-full cursor-pointer select-none">
              <span className="flex-shrink-0"><Checkbox id="marketing" /></span>
              <span className="text-sm md:text-base">[선택] 홍보성 이벤트 SMS 수신 동의</span>
            </label>
          </div>

          {/* 전역 메시지 */}
          {err && <p className="text-red-600 text-sm whitespace-pre-line">{err}</p>}
          {okMsg && <p className="text-green-600 text-sm">{okMsg}</p>}

          {/* 제출 */}
          <div className="sm:col-span-2 flex justify-end">
            <button
              className="min-w-[120px] px-4 py-3 rounded-xl bg-[#5F9BFF] text-white disabled:bg-[#F1F1F1] disabled:text-gray-400"
              disabled={loading || !canSubmit}
            >
              {loading ? "등록 중..." : "회원 등록"}
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
}


function WorkplaceManageCard() {
  const { user } = useAdminAuth();
  const isSuper = user?.role === "super";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");

  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // 🔹 목록 접기/펼치기 상태
  const [collapsed, setCollapsed] = useState(false);

  // 리스트 불러오기
  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const data = await fetchWorkplaces(); // GET /workplaces
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(formatApiError(e, "사업장 목록을 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuper) return;
    load();
  }, [isSuper]);

  if (!isSuper) return null;

  const canSubmit = code.trim() && name.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOkMsg("");

    if (!code.trim() || !name.trim()) {
      setErr("코드와 이름을 모두 입력하세요.");
      return;
    }

    try {
      setLoading(true);
      await createWorkplace({
        code: code.trim(),          // 예: "gangnam"
        name: name.trim(),          // 예: "온핏 강남점"
        address: address.trim() || null,
        is_active: true,
      });
      setOkMsg("사업장이 등록되었습니다.");
      setCode("");
      setName("");
      setAddress("");
      await load();
    } catch (e) {
      setErr(formatApiError(e, "사업장 등록에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (wp) => {
    try {
      setLoading(true);
      setErr("");
      setOkMsg("");
      await updateWorkplace(wp.id, { is_active: !wp.is_active });
      await load();
    } catch (e) {
      setErr(formatApiError(e, "상태 변경에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  // 🔹 삭제 핸들러
  const handleDelete = async (wp) => {
    const ok = window.confirm(
      `'${wp.name}' 사업장을 삭제할까요?\n(이미 등록된 회원/관리자의 사업장 값은 그대로 남을 수 있습니다.)`
    );
    if (!ok) return;

    try {
      setLoading(true);
      setErr("");
      setOkMsg("");
      await deleteWorkplace(wp.id); // DELETE /workplaces/{id}
      setOkMsg("사업장을 삭제했습니다.");
      await load();
    } catch (e) {
      setErr(formatApiError(e, "사업장 삭제에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="사업장 등록 / 관리">
      <div className="space-y-4">
        {/* 등록 폼 */}
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">
              코드<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder='예: "gangnam", "sinnonhyeon" 등 내부용 코드'
            />
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">
              사업장 이름<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='예: "싸이핏 강남점"'
            />
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium mb-1">
              주소 (선택)
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="도로명 주소 등"
            />
          </div>

          <div className="md:col-span-3 flex justify-end">
            <button
              type="submit"
              className="min-w-[120px] px-4 py-3 rounded-xl bg-[#5F9BFF] text-white disabled:bg-[#F1F1F1] disabled:text-gray-400"
              disabled={loading || !canSubmit}
            >
              {loading ? "저장 중..." : "사업장 등록"}
            </button>
          </div>
        </form>

        {/* 에러 / 성공 메시지 */}
        {err && <p className="text-red-600 text-sm whitespace-pre-line">{err}</p>}
        {okMsg && <p className="text-green-600 text-sm">{okMsg}</p>}

        {/* 리스트 */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              등록된 사업장 <span className="font-semibold">{items.length}</span>개
            </span>

            {/* 🔹 로딩 + 접기/펼치기 버튼 */}
            <div className="flex items-center gap-2">
              {loading && <span className="text-xs text-gray-400">불러오는 중...</span>}
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
              >
                {collapsed ? "목록 펼치기" : "목록 접기"}
              </button>
            </div>
          </div>

          {/* 🔹 접혀 있을 때 안내 문구 */}
          {collapsed && items.length > 0 && (
            <div className="text-xs text-gray-400 mb-1">
              목록이 접혀 있습니다.
            </div>
          )}

          {/* 실제 목록: 접혀 있으면 숨김 */}
          {!collapsed && (
            <div className="border rounded-lg divide-y bg-gray-50">
              {items.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  등록된 사업장이 없습니다.
                </div>
              ) : (
                items.map((wp) => (
                  <div
                    key={wp.id}
                    className="p-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{wp.name}</div>
                      <div className="text-xs text-gray-500 truncate">코드: {wp.code}</div>
                      {wp.address && (
                        <div className="text-xs text-gray-400 truncate">{wp.address}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={clsx(
                          "px-2 py-1 rounded-full text-xs",
                          wp.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-600"
                        )}
                      >
                        {wp.is_active ? "사용 중" : "중지"}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(wp)}
                        disabled={loading}
                        className="px-3 py-1 rounded-lg border text-xs bg-white hover:bg-gray-50 disabled:opacity-60"
                      >
                        {wp.is_active ? "중지" : "사용"}
                      </button>

                      {/* 🔹 삭제 버튼 */}
                      <button
                        type="button"
                        onClick={() => handleDelete(wp)}
                        disabled={loading}
                        className="px-3 py-1 rounded-lg border border-red-200 text-xs text-red-600 bg-white hover:bg-red-50 disabled:opacity-60"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}



/* =========================
   계정 추가 페이지 (역할에 따라 섹션 노출 변경)
   ========================= */
function AddAccountsPage() {
  const { user } = useAdminAuth();
  const role = user?.role;
  return (
    <div className="space-y-5">
      {role === "super" && <AdminCreateForm />}
      <UserCreateForm />
      {role === "super" && <WorkplaceManageCard />}
    </div>
  );
}

/* =========================
   라우터
   ========================= */
export default function AdminRouter() {
  return (
    <AdminAuthProvider>
      <Routes>
        {/* 계정 추가 화면을 기본 대시보드로 설정 */}
        <Route
          path="/"
          element={
            <RequireAuth roles={["admin", "super"]}>
              <Shell>
                <AddAccountsPage />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/members/update"
          element={
            <RequireAuth roles={["admin", "super"]}>
              <Shell><MemberUpdate /></Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/measurements"
          element={
            <RequireAuth roles={["admin", "super"]}>
              <Shell><Measurements /></Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/workout-settings"
          element={
            <RequireAuth roles={["admin", "super"]}>
              <Shell><WorkoutSettings /></Shell>
            </RequireAuth>
          }
        />

        {/* 로그인 페이지 */}
        <Route path="/login" element={<AdminLogin />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}

/* =========================
   간단 보호 라우트 (useAdminAuth 사용)
   ========================= */
function RequireAuth({ children, roles }) {
  const { isAuthenticated, hasRole, booting } = useAdminAuth();
  const location = useLocation();
  if (booting) return null;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace state={{ from: location }} />;
  if (roles && !hasRole(roles)) return <Navigate to="/admin" replace />;
  return children;
}

/* =========================
   로그인 페이지 (오류 문구/로딩 포함)
   ========================= */
function AdminLogin() {
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAdminAuth();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!id.trim() || !pw.trim()) {
      setErr("아이디와 비밀번호를 입력하세요.");
      return;
    }
    try {
      setLoading(true);
      const { ok } = await login({ id, password: pw });
      if (ok) {
        const to = location.state?.from?.pathname || "/admin";
        nav(to, { replace: true });
      }
    } catch (e) {
      setErr(e?.status === 401 ? "아이디 또는 비밀번호가 올바르지 않습니다." : formatApiError(e, "로그인 실패"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gray-50">
      <div className="w-full max-w-md bg-white border rounded-2xl shadow-sm p-6">
        <h1 className="text-xl font-semibold mb-4">관리자 로그인</h1>
        <form className="space-y-3" onSubmit={onSubmit}>
          <div>
            <label className="text-sm text-gray-600">아이디</label>
            <input
              className="mt-1 w-full h-10 rounded-xl border px-3"
              value={id}
              onChange={(e) => { setId(e.target.value); if (err) setErr(""); }}
              placeholder='예: "super" 또는 "admin01"'
              autoComplete="username"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">비밀번호</label>
            <div className="mt-1 flex">
              <input
                type={showPw ? "text" : "password"}
                className="flex-1 h-10 rounded-l-xl border px-3"
                value={pw}
                onChange={(e) => { setPw(e.target.value); if (err) setErr(""); }}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="px-3 border-y border-r rounded-r-xl text-sm"
              >
                표시
              </button>
            </div>
          </div>
          {err && <p className="text-red-600 text-sm whitespace-pre-line">{err}</p>}
          <button className="w-full h-11 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-60" disabled={loading}>
            {loading ? "로그인 중..." : "로그인"}
          </button>
        </form>
      </div>
    </div>
  );
}
